import { useCompanyApi } from '@/api/client/api-client';
import {
    DateSeparatedMessageType,
    type Conversation,
    type ConversationBranch,
    type ConversationClient,
    type ConversationClientSearchResult,
    type ConversationDetails,
    type ConversationFileSearchResult,
    type DateSeparatedMessage,
    type GetConversationSearchResults,
    type PostMessagePayload,
    type SmsMessage,
    type UnreadSmsCount,
} from '@/api/interfaces/texting.types';
import { notifyError } from '@/bugsnag';
import { appendSignature, getDraftKey, isConversationStarted } from '@/components/Views/Company/Messages/texting-utils';
import { useConditionalInterval } from '@/shared/composables/use-conditional-interval';
import { ONE_SECOND } from '@/shared/constants/time';
import { isAbortError, isErrorPolyfill } from '@/shared/utils/errors';
import { useRouteMatch } from '@/shared/utils/router';
import { useFeatureFlagsStore } from '@/stores/company/feature-flags.store';
import { useCurrentUserStore } from '@/stores/current-user.store';
import dayjs from 'dayjs';
import { defineStore } from 'pinia';
import { computed, reactive, ref, watch, type ComputedRef, type Ref } from 'vue';

const FALLBACK_POLLING_INTERVAL_MS = ONE_SECOND * 20;
const POLLING_INTERVAL_MS = parseInt(import.meta.env.VITE_MESSAGING_POLLING_INTERVAL_MS, 10) || FALLBACK_POLLING_INTERVAL_MS;
const companyFetch = useCompanyApi({ auth: 'jwt' });

const initUnreadConversationsCountPolling = (isTextingEnabled: ComputedRef<boolean>) => {
    const {
        data,
        error: unreadConvoError,
        execute: getLatestNotifications,
    } = companyFetch('/api/conversations/notifications', { immediate: false }).json<UnreadSmsCount>();

    useConditionalInterval(getLatestNotifications, {
        condition: isTextingEnabled,
        interval: POLLING_INTERVAL_MS,
        immediateCallback: true,
    });

    watch(unreadConvoError, filteredErrorHandler);

    const unreadCount = computed(() => data.value?.unread_count ?? 0);
    return unreadCount;
};

/**
 * @description
 * With our polling interval and computed properties, we're going to get a lot of
 * {@link [AbortError](https://developer.mozilla.org/en-US/docs/Web/API/AbortSignal)}s
 * as we're cancelling requests when the user is doing something else, like searching or
 * switching conversations, so we'll filter them out and log them at debug level.
 */
const filteredErrorHandler = <T>(err: T) => {
    if (!err) return;

    const isError = isErrorPolyfill(err);

    if (isError && isAbortError(err)) {
        // Log AbortErrors at debug level - these are expected cancellations
        return console.debug('Request cancelled:', err);
    }

    notifyError(err, 'texting.store:filteredErrorHandler');
};

// this is often called the first stage of search. It returns a list of clients and files that match the search term.
const searchConversations = () => {
    const searchTerm = ref('');

    watch(searchTerm, () => {
        data.value = null; // clear any prior search results when term changes
    });

    const {
        data,
        error: searchError,
        isFetching: isSearchingByTerm,
        execute,
    } = companyFetch(() => `/api/conversations/v2/search?q=${encodeURIComponent(searchTerm.value)}`, {
        beforeFetch(ctx) {
            if (!searchTerm.value) {
                ctx.cancel();
            }
        },
    }).json<GetConversationSearchResults>();

    watch(searchError, filteredErrorHandler);

    const clientsSearchResults = computed(() => data.value?.clients ?? []);
    const filesSearchResults = computed(() => data.value?.files ?? []);
    const isSearchComplete = computed<boolean>(() => Array.isArray(data.value?.clients ?? data.value?.files));

    return {
        clientsSearchResults,
        filesSearchResults,
        isSearchingByTerm,
        isSearchComplete,
        searchTerm,
        search: execute,
    };
};

// this is often called the second stage of search. It returns a list of conversations that match the selected search result.
const refineSearch = (selectedSearchResult: Ref<ConversationClientSearchResult | ConversationFileSearchResult | undefined>) => {
    const initialData: Conversation[] = [];
    const queryParams = computed(() => {
        const selected = selectedSearchResult.value;

        if (selected) {
            const paramId = 'ref_number' in selected ? ('file_ids[]' as const) : ('client_ids[]' as const);
            return `${paramId}=${selected.id}`;
        }

        return '';
    });

    const {
        data,
        error: refinementError,
        isFetching,
    } = companyFetch(() => `/api/conversations/v2/getByFileOrClientId?${queryParams.value}`, {
        immediate: false,
        initialData,
        refetch: true,
        beforeFetch(ctx) {
            if (!queryParams.value) {
                ctx.cancel();
                data.value = initialData;
            }
        },
    }).json<Conversation[]>();

    watch(refinementError, filteredErrorHandler);

    return {
        refinedSearchResults: data as Ref<typeof initialData>,
        isFindingPossibleConvosFromSearchResult: isFetching,
    };
};

const getConversations = (isOnMessageRoute: Ref<boolean>): Ref<Conversation[]> => {
    const {
        data: conversations,
        error: conversationsError,
        execute: getLatestConversations,
    } = companyFetch('/api/conversations/v2', {
        immediate: false,
        initialData: [] satisfies Conversation[],
    }).json<Conversation[]>();

    useConditionalInterval(getLatestConversations, {
        interval: POLLING_INTERVAL_MS,
        condition: isOnMessageRoute,
        immediateCallback: true,
    });

    watch(conversationsError, filteredErrorHandler);

    return conversations as Ref<Conversation[]>; // initialData guarantees this will be set
};

const getConversationMessages = (
    activeConversation: Ref<Conversation | undefined>,
    isSwitchingConvo: Ref<boolean>,
    isOnMessageRoute: Ref<boolean>,
) => {
    const params = computed(() => ({
        branchPhone: encodeURIComponent(activeConversation.value?.branch.phone_number ?? ''),
        conversationId: activeConversation.value?.id,
    }));
    const {
        data: _messages,
        error: messagesError,
        execute: getLatestMessages,
        isFetching: isFetchingMessages,
        isFinished,
    } = companyFetch(
        () => `/api/conversations/v3/${params.value.branchPhone}/messages/?conversation_id=${params.value.conversationId}`,
        {
            refetch: true,
            beforeFetch(ctx) {
                const { branchPhone, conversationId } = params.value;
                if (!branchPhone || !conversationId) {
                    ctx.cancel();
                }
            },
        },
    ).json<SmsMessage[]>();

    useConditionalInterval(getLatestMessages, {
        interval: POLLING_INTERVAL_MS,
        condition: isOnMessageRoute,
        immediateCallback: true,
    });

    watch(isFinished, isNowFinished => {
        if (isNowFinished) isSwitchingConvo.value = false;
    });

    watch(messagesError, filteredErrorHandler);

    const messages = computed<SmsMessage[]>(() => {
        if (isSwitchingConvo.value || !_messages.value) return [];
        return _messages.value.reverse();
    });

    return {
        isFetchingMessages,
        messages,
        getLatestMessages,
    };
};

const getConversationByBranchPhoneAndClientPhone = async (
    branchPhone: ConversationBranch['phone_number'],
    clientPhone: ConversationClient['phone_number'],
) => {
    const { data: conversation, error: conversationError } = await companyFetch(
        `/api/conversations/v2/${branchPhone}/${clientPhone}`,
    ).json<Conversation>();

    watch(conversationError, filteredErrorHandler, { immediate: true });

    return conversation.value;
};

const addDateSeparatorsToMessages = (messages: Ref<SmsMessage[]>) => {
    return computed(() => {
        const today = dayjs();
        const mixedMessages: DateSeparatedMessage[] = [];

        if (!messages.value?.length) {
            return mixedMessages;
        }

        let prevDate: SmsMessage['created_at'] = dayjs().add(1, 'day').toString(); // init to tomorrow so there's always something to compare to

        for (const sms of messages.value) {
            const smsDateDayJs = dayjs(sms.created_at);

            if (!smsDateDayJs.isSame(dayjs(prevDate), 'day')) {
                let separatorString = '';

                if (smsDateDayJs.isSame(today, 'day')) {
                    separatorString = 'Today';
                } else if (smsDateDayJs.isSame(today.subtract(1, 'day'), 'day')) {
                    separatorString = 'Yesterday';
                } else {
                    separatorString = smsDateDayJs.format('dddd, MMM. D');
                }

                mixedMessages.push({ type: DateSeparatedMessageType.DATE_SEPARATOR, date: separatorString });
            }

            mixedMessages.push({ type: DateSeparatedMessageType.MESSAGE, sms });
            prevDate = sms.created_at;
        }

        return mixedMessages;
    });
};

const markLatestMessageAsRead = (
    messages: Ref<SmsMessage[]>,
    activeConversation: Ref<Conversation | undefined>,
    conversations: Ref<Conversation[]>,
) => {
    const lastMessageId = computed(() => messages.value[messages.value.length - 1]?.id);
    const { error: markMessagesError, isFinished } = companyFetch(
        () => `/api/conversations/messages/read/${lastMessageId.value}`,
        {
            immediate: false,
            refetch: true,
            beforeFetch(ctx) {
                if (!lastMessageId.value) {
                    ctx.cancel();
                }
            },
        },
    )
        .post()
        .json<{ message: string }>();

    watch(markMessagesError, filteredErrorHandler);

    watch(isFinished, isNowFinished => {
        const activeConvoId = activeConversation.value?.id;
        if (isNowFinished && activeConvoId && lastMessageId.value) {
            clearActiveConversationNumUnreadCount(activeConvoId, conversations);
        }
    });
};

const clearActiveConversationNumUnreadCount = (activeConvoId: Conversation['id'], conversations: Ref<Conversation[]>) => {
    const convoCopy = conversations.value.slice();
    const activeIndex = convoCopy.findIndex(({ id }) => id === activeConvoId);
    if (activeIndex > -1) {
        convoCopy[activeIndex].num_unread = 0;
    }
    conversations.value = convoCopy;
};

const initPostMessageApi = (
    activeConversation: Ref<Conversation | undefined>,
    currentUserStore: ReturnType<typeof useCurrentUserStore>,
    getLatestMessages: ReturnType<typeof getConversationMessages>['getLatestMessages'],
) => {
    const {
        data: convoId,
        error: postMessageError,
        isFetching: isSending,
        post,
    } = companyFetch('/api/conversations/send-message', { immediate: false }).json<{
        id: number;
    }>();

    watch(postMessageError, filteredErrorHandler);

    return {
        isSending,
        sendMessage(message: string) {
            const activeConvo = activeConversation.value;

            if (!activeConvo) return;

            const currentUserFirstName = currentUserStore.currentUser?.name?.split(' ')[0];
            const payload: PostMessagePayload = {
                client_ids: activeConvo.clients.map(({ id }) => id),
                message: appendSignature(message, activeConvo, currentUserFirstName),
                messaging_phone: activeConvo.branch.phone_number ?? '',
            };

            post(payload)
                .execute()
                .then(() => {
                    const id = convoId.value?.id;
                    if (id) {
                        activeConvo.id = id; // if sending started a conversation, the active convo now has a non-null id
                        getLatestMessages(); // get the message we just attempted to send (might have failed)
                    }
                });
        },
    };
};

const getConversationDetails = (activeConversation: Ref<Conversation | undefined>) => {
    const params = computed(() => ({
        branchPhone: activeConversation.value?.branch.phone_number ?? '',
        activeConversation: activeConversation.value?.clients?.map(client => `client_id_list[]=${client.id}`).join('&') ?? [],
    }));
    const getConversationDetailsEndpoint = computed(
        () => `/api/conversations/v3/${params.value.branchPhone}/details?${params.value.activeConversation}`,
    );
    const { data: _details, error: detailsError } = companyFetch(getConversationDetailsEndpoint, {
        immediate: false,
        refetch: true,
        beforeFetch(ctx) {
            const clientIds = activeConversation.value?.clients?.map(client => client.id) || [];
            if (!clientIds.length) {
                ctx.cancel();
            }
        },
    }).json<ConversationDetails>();

    watch(detailsError, filteredErrorHandler);

    return computed<Partial<ConversationDetails>>(() => _details.value ?? {});
};

export const useTextingStore = defineStore('texting', () => {
    // other pinia stores
    const currentUserStore = useCurrentUserStore();
    const featureFlagsStore = useFeatureFlagsStore();

    // polling guards
    const isTextingEnabled = computed(() => featureFlagsStore.flags?.texting === 'enabled');
    const isOnMessageRoute = useRouteMatch('/user/messages');
    const isTextingEnabledOnMessageRoute = computed(() => isTextingEnabled.value && isOnMessageRoute.value);

    // texting store data
    const activeConversation = ref<Conversation>();
    const areDetailsShowing = ref(false);
    const selectedSearchResult = ref<ConversationClientSearchResult | ConversationFileSearchResult>();
    const isSwitchingConvo = ref(false);

    const messageDrafts = reactive<Record<ReturnType<typeof getDraftKey>, SmsMessage['message']>>({});
    const activeDraftKey = computed(() => activeConversation.value && getDraftKey(activeConversation.value));
    const activeDraft = computed<string>({
        get() {
            const draft = activeDraftKey.value ? messageDrafts[activeDraftKey.value] : '';
            return draft ?? '';
        },
        set(text) {
            if (activeDraftKey.value) messageDrafts[activeDraftKey.value] = text;
        },
    });

    // fetch requests setup
    const { search, searchTerm, isSearchingByTerm, isSearchComplete, filesSearchResults, clientsSearchResults } =
        searchConversations();
    const conversations = getConversations(isTextingEnabledOnMessageRoute);

    const details = getConversationDetails(activeConversation);
    const { isFetchingMessages, messages, getLatestMessages } = getConversationMessages(
        activeConversation,
        isSwitchingConvo,
        isTextingEnabledOnMessageRoute,
    );
    const messagesWithDateSeparators = addDateSeparatorsToMessages(messages);
    const { isFindingPossibleConvosFromSearchResult, refinedSearchResults } = refineSearch(selectedSearchResult);
    markLatestMessageAsRead(messages, activeConversation, conversations);
    const { isSending, sendMessage } = initPostMessageApi(activeConversation, currentUserStore, getLatestMessages);

    // actions
    const setActiveConversation = (conversation: Conversation) => {
        if (isConversationStarted(conversation) && conversation.id === activeConversation.value?.id) {
            return;
        }
        activeConversation.value = conversation;
        isSwitchingConvo.value = true;
        areDetailsShowing.value = !!conversation;
    };

    const openRemoteConversation = async (
        branchPhone: ConversationBranch['phone_number'],
        clientPhone: ConversationClient['phone_number'],
    ) => {
        isSwitchingConvo.value = true;
        const convo = await getConversationByBranchPhoneAndClientPhone(branchPhone, clientPhone);
        if (convo) setActiveConversation(convo);
        else isSwitchingConvo.value = false;
    };

    const unreadConversationsCount = initUnreadConversationsCountPolling(isTextingEnabled);

    return {
        unreadConversationsCount,
        activeConversation: computed(() => activeConversation.value),
        isSwitchingConvo,
        areDetailsShowing,
        conversations,
        details,
        isFetchingMessages,
        messagesWithDateSeparators,
        activeDraft,
        clientsSearchResults,
        filesSearchResults,
        isSearchComplete,
        isSearchingByTerm,
        selectedSearchResult,
        isFindingPossibleConvosFromSearchResult,
        refinedSearchResults,
        searchTerm,
        isSending,
        search,
        setActiveConversation,
        openRemoteConversation,
        sendMessage,
    };
});

export const getPresignedTranscriptUrl = (conversationId: Ref<Conversation['id'] | undefined>) => {
    const {
        data: transcript,
        error: transcriptError,
        isFetching,
        execute,
    } = companyFetch(() => `/api/conversations/transcript/${conversationId.value}`, {
        immediate: false,
        beforeFetch(ctx) {
            if (!conversationId.value) {
                ctx.cancel();
            }
        },
    }).json<{
        presigned_url: string;
    }>();

    watch(transcriptError, filteredErrorHandler);

    const downloadTranscript = async () => {
        await execute();

        const presignedUrl = transcript.value?.presigned_url;
        if (!presignedUrl) {
            notifyError(new Error('No presigned URL returned'), 'texting.store:getPresignedTranscriptUrl');
            return;
        }

        const link = document.createElement('a');
        link.href = presignedUrl;
        link.click();
    };

    return {
        isFetching,
        downloadTranscript,
    };
};

export const __tests__ = {
    addDateSeparatorsToMessages,
    markLatestMessageAsRead,
    getConversationByBranchPhoneAndClientPhone,
};
